import type React from 'react';

import type { ColorName } from 'styles/config/colors';
import { baseColors } from 'styles/config/colors';

import { cn } from 'utils/cn';
import { colorCssVar } from 'utils/colorCssVar';
import { InfoIcon } from '../Icons';
import Tooltip from '../Tooltip';

export enum LegacyTagVariant {
  DEFAULT = 'DEFAULT',
  ACTIVE = 'ACTIVE',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
  KEYWORD = 'KEYWORD',
  INFO = 'INFO',
  GRAY = 'GRAY',
  WARNING = 'WARNING',
}

export interface LegacyTagProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  append?: React.ReactNode;
  prepend?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
  textColor?: ColorName;
  variant?: LegacyTagVariant;
  iconOnly?: boolean;
  infoContent?: React.ReactNode;
  hideBorder?: boolean;
  textDataTestId?: string;
}

const getVariantBgColor = (variant: LegacyTagVariant): string => {
  switch (variant) {
    case LegacyTagVariant.ACTIVE:
      return colorCssVar('scriptingNode');
    case LegacyTagVariant.ERROR:
      return colorCssVar('backgroundDanger');
    case LegacyTagVariant.SUCCESS:
      return colorCssVar('backgroundSuccess');
    case LegacyTagVariant.KEYWORD:
      return colorCssVar('backgroundInverse');
    case LegacyTagVariant.GRAY:
      return baseColors.grey[70];
    case LegacyTagVariant.INFO:
      return baseColors.blue['05'];
    case LegacyTagVariant.WARNING:
      return baseColors.yellow['05'];
    default:
      return colorCssVar('backgroundPage');
  }
};

const getVariantBorderColor = (
  variant: LegacyTagVariant,
): string | undefined => {
  if (variant === LegacyTagVariant.INFO) return baseColors.blue['20'];
  if (variant === LegacyTagVariant.WARNING) return baseColors.yellow['20'];
  return undefined;
};

const isInverseTextVariant = (variant: LegacyTagVariant): boolean =>
  [
    LegacyTagVariant.ERROR,
    LegacyTagVariant.SUCCESS,
    LegacyTagVariant.ACTIVE,
    LegacyTagVariant.KEYWORD,
    LegacyTagVariant.GRAY,
  ].includes(variant);

/**
 * @deprecated Use `Tag` from `components/atoms/Tag` instead.
 */
const LegacyTag: React.FC<LegacyTagProps> = ({
  append,
  prepend,
  onClick,
  children,
  className,
  textColor,
  variant = LegacyTagVariant.DEFAULT,
  iconOnly,
  infoContent,
  hideBorder,
  textDataTestId = 'tag-text',
  ...props
}) => {
  const borderColor = getVariantBorderColor(variant);
  const isKeyword = variant === LegacyTagVariant.KEYWORD;
  const isInfo = variant === LegacyTagVariant.INFO;
  const isWarning = variant === LegacyTagVariant.WARNING;

  return (
    <aside
      className={cn(
        'inline-flex gap-xxs2 items-center justify-end',
        'h-sm3',
        iconOnly ? 'p-xs1' : 'px-xs2',
        'bg-background-page border border-border-strong rounded-xSmall',
        // Descendant text-size cascade — pre-migration styled-components used
        // a `p {}` descendant selector that forced 12px on any nested <p>
        // (including <Text> which renders as <p>). Mirror that here for <p>
        // and <span> (Text's bodySmall variant element).
        '[&_p]:text-body-small [&_span]:text-body-small',
        isInverseTextVariant(variant) &&
          '[&_p]:text-primary-inverse [&_span]:text-primary-inverse',
        isInfo &&
          '[&_p]:text-information-text-01 [&_span]:text-information-text-01',
        isWarning && '[&_p]:text-warning [&_span]:text-warning',
        !hideBorder || 'border-none',
        onClick && 'cursor-pointer',
        isKeyword && 'mr-2',
        (isInfo || isWarning) && 'gap-xs1 px-xs2',
        className,
      )}
      style={{
        backgroundColor: getVariantBgColor(variant),
        ...(borderColor && { borderColor }),
        ...(hideBorder && { border: 'none' }),
      }}
      onClick={onClick}
      {...props}
    >
      {prepend}
      <p
        className={cn(
          'flex items-center w-max text-body-small font-semibold leading-tight text-secondary',
          append &&
            cn(
              'overflow-hidden max-w-xxl1 text-ellipsis whitespace-nowrap text-primary',
            ),
          isInverseTextVariant(variant) && 'text-primary-inverse',
          isInfo && 'text-information-text-01',
          isWarning && 'text-warning',
        )}
        data-test-id={textDataTestId}
        style={textColor ? { color: colorCssVar(textColor) } : undefined}
      >
        {children}
      </p>
      {append}
      {variant === LegacyTagVariant.INFO && infoContent && (
        <Tooltip content={infoContent} placement="top" complex={true}>
          <InfoIcon color="iconInfo" />
        </Tooltip>
      )}
    </aside>
  );
};

export { LegacyTag };
